import { createClient } from '$lib/prismicio';
import type { BuildQueryURLArgs, DateField, Ordering } from '@prismicio/client';
import type { AvailableDocumentTypes, AvailableTypeHandles, PaginationResponse } from './app.types';

export const getPaginatedDocument = async (
	type: AvailableTypeHandles,
	itemsPerPage: number,
	pageNum: number,
	nextPageAvailable: boolean,
	filters: string[] = [],
	orderings: Ordering[] = []
): Promise<PaginationResponse | null> => {
	if (!nextPageAvailable) return null;

	const client = createClient();
	let results: AvailableDocumentTypes = [];
	let nextPage = null;
	const options: Partial<BuildQueryURLArgs> = {
		pageSize: itemsPerPage,
		page: pageNum,
		filters: filters
	};

	if (orderings.length) {
		options.orderings = orderings;
	}

	const documentType = type as AvailableTypeHandles;
	const documents = await client.getByType(documentType, options);

	results = documents.results as AvailableDocumentTypes;
	nextPage = documents.next_page !== null ? true : false;

	return {
		results,
		nextPage,
		pageNum
	};
};

export function combinePageArrays<T>(...arrays: T[][]): T[] {
	return arrays.flat();
}

export const siteDateFormat: string = 'en-US';
export const newsDateFormat: Intl.DateTimeFormatOptions = {
	month: 'long',
	day: 'numeric',
	year: 'numeric'
};
export const gameDateFormat: Intl.DateTimeFormatOptions = {
	month: 'long',
	day: 'numeric',
	year: 'numeric'
};

export const gameTimeFormat: Intl.DateTimeFormatOptions = {
	hour: '2-digit',
	minute: '2-digit',
	hour12: false
};

export const getFormattedDate = (
	date: string | DateField,
	locale: string,
	format: Intl.DateTimeFormatOptions
) => {
	let formattedDate = 'Date Format Error';
	const publishedDate = date ? new Date(date) : false;
	if (publishedDate) {
		formattedDate = publishedDate.toLocaleDateString(locale, format);
	}

	return formattedDate;
};

export function publicationDateToISO(date: DateField | Date | string) {
	const dateString = date as string;
	const dateObject = new Date(dateString);
	return dateObject.toISOString();
}

export const getGameDate = (date: string | DateField, locale: string) => {
	let formattedDate = 'Date Format Error';
	const dateObject = date ? new Date(date) : false;
	if (dateObject) {
		formattedDate = dateObject.toLocaleDateString(locale, gameDateFormat);
	}

	return formattedDate;
};

export const getGameTime = (date: string | DateField, locale: string) => {
	let formattedTime = 'Time Format Error';
	const dateObject = date ? new Date(date) : false;
	if (dateObject) {
		formattedTime = dateObject.toLocaleTimeString(locale, gameTimeFormat);
	}

	return formattedTime;
};

export const calculateGameEnd = (date: string | DateField) => {
	const dateObject = date ? new Date(date) : false;
	if (dateObject) {
		const d = new Date(dateObject.getTime() + 2 * 60 * 60 * 1000);
		const pad = (n: number) => String(n).padStart(2, '0');

		const year = d.getFullYear();
		const month = pad(d.getMonth() + 1);
		const day = pad(d.getDate());
		const hours = pad(d.getHours());
		const minutes = pad(d.getMinutes());
		const seconds = pad(d.getSeconds());

		const offsetMinutes = -d.getTimezoneOffset();
		const sign = offsetMinutes >= 0 ? '+' : '-';
		const offsetHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
		const offsetMins = pad(Math.abs(offsetMinutes) % 60);

		return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
	}

	return date;
};
